import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AppSettings } from '../../config/config';

export type LikeSlug = 'like' | 'love' | 'laugh' | 'celebration' | null;

export interface ActivityMediaItem {
  mediaId: string;
  mediaUrl: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  mediaSubType: string;
}

// Parent feed item — no createdByUserId / activityLevel / createdByBlockedStatus
// documented for the parent response (unlike the staff ActivityFeedItem):
// parents never own posts, so ownership fields aren't relevant here.
export interface ParentActivityFeedItem {
  activityId: number;
  createdByUserName: string;
  createdByUserImage: string;
  createdByUserDesignation: string;
  CreatedOn: string;
  activityDescription: string;
  likesCount: number;
  commentsCount: number;
  userLikedStatus: LikeSlug;
  lastLikedByUserName: string | null;
  uniqueFeedLikeTypes: string[];
  media: {
    images: ActivityMediaItem[];
    videos: ActivityMediaItem[];
    documents: ActivityMediaItem[];
  };
  [key: string]: any;
}

export interface GetActivityDataResponse {
  status: boolean;
  body: {
    maxPageNumber: number;
    activities: ParentActivityFeedItem[];
  };
}

export interface LikePostBody {
  lang: string;
  classId: string;
  studentId: string;
  activityId: string;
  likeSlug: LikeSlug;
}

export interface LikePostResponse {
  status: boolean;
  body: {
    message: string;
    uniqueLikeTypes: string[];
  };
}

// No userId/userType/createdByBlockedStatus documented for the parent
// response (unlike staff's ActivityLikeEntry).
export interface ParentActivityLikeEntry {
  likedByUserName: string;
  likedByUserImage: string | null;
  likedByUserDesignation: string;
  likeType: 'like' | 'love' | 'laugh' | 'celebration';
}

export interface GetReactionsResponse {
  status: boolean;
  body: {
    activityLikesStat: { like: number; love: number; laugh: number; celebration: number };
    maxPageNumber: number;
    likes: ParentActivityLikeEntry[];
  };
}

export interface DoCommentBody {
  lang: string;
  classId: string;
  studentId: string;
  activityId: string;
  comment: string;
}

export interface ParentActivityComment {
  commentId: string;
  comment: string;
  commentByUserId: string;
  commentByUserName: string;
  commentByUserImage: string;
  commentByUserDesignation: string;
  commentedOn: string;
  [key: string]: any;
}

export interface GetActivityCommentResponse {
  status: boolean;
  body: {
    activityComments: ParentActivityComment[];
    maxPageNumber: number;
  };
}

export interface DeleteCommentBody {
  commentId: string;
  studentId: string;
  classId: string;
  lang: string;
}

export interface EditCommentBody {
  commentId: string;
  comment: string;
  studentId: string;
  classId: string;
  lang: string;
}

export interface SimpleMessageResponse {
  status: boolean;
  body: {
    message: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ParentActivityService {
  private url = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) {}

  private authHeaders(): HttpHeaders | undefined {
    const raw = localStorage.getItem('authToken');
    if (!raw) { return undefined; }
    try {
      return new HttpHeaders().append('authToken', JSON.parse(raw));
    } catch {
      return new HttpHeaders().append('authToken', raw);
    }
  }

  getActivityData(classId: string, studentId: string, pageNumber: number) {
    const params = new HttpParams()
      .set('lang', 'en')
      .set('classId', classId)
      .set('studentId', studentId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', 10);

    return this.http.get<GetActivityDataResponse>(`${this.url}/get/activities/list/parent`, {
      params,
      headers: this.authHeaders(),
    });
  }

  likePost(body: LikePostBody) {
    return this.http.post<LikePostResponse>(`${this.url}/action/like/activity/parent`, body, {
      headers: this.authHeaders(),
    });
  }

  getReactions(classId: string, studentId: string, activityId: string, likeType: 'ALL' | LikeSlug, pageNumber: number) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('studentId', studentId)
      .set('lang', 'en')
      .set('activityId', activityId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', 10)
      .set('likeType', likeType || 'ALL');

    return this.http.get<GetReactionsResponse>(`${this.url}/get/like/activity/parent`, {
      params,
      headers: this.authHeaders(),
    });
  }

  doComment(body: DoCommentBody) {
    return this.http.post<SimpleMessageResponse>(`${this.url}/action/comment/activity/parent`, body, {
      headers: this.authHeaders(),
    });
  }

  getActivityComment(classId: string, studentId: string, activityId: string, pageNumber: number, pageLimit: number = 10) {
    const params = new HttpParams()
      .set('lang', 'en')
      .set('classId', classId)
      .set('studentId', studentId)
      .set('activityId', activityId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', pageLimit);

    return this.http.get<GetActivityCommentResponse>(`${this.url}/get/comment/activity/parent`, {
      params,
      headers: this.authHeaders(),
    });
  }

  editActivityComment(body: EditCommentBody) {
    return this.http.put<SimpleMessageResponse>(`${this.url}/update/comment/activity/parent`, body, {
      headers: this.authHeaders(),
    });
  }

  deleteComment(body: DeleteCommentBody) {
    return this.http.delete<{ status: boolean }>(`${this.url}/delete/comment/activity/parent`, {
      body,
      headers: this.authHeaders(),
    });
  }
}
